import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Menu, X } from "lucide-react";
import { BookingModal } from "./BookingModal";
import { ServiceSelectionModal } from "./ServiceSelectionModal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getOptimizedImageUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";

interface NavigationProps {
  photographerName?: string;
  logoUrl?: string;
}

interface Profile {
  display_name: string;
  logo_url: string | null;
}

export function Navigation({ photographerName: propPhotographerName, logoUrl: propLogoUrl }: NavigationProps) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServiceSelectionOpen, setIsServiceSelectionOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('photographer_profiles')
        .select('display_name, logo_url')
        .limit(1)
        .maybeSingle();

      if (data) {
        setProfile(data);
      }
    };

    fetchProfile();
  }, []);

  const photographerName = profile?.display_name || propPhotographerName || "Amadeo Photography";
  const logoUrl = profile?.logo_url || propLogoUrl;

  const handleBookSession = () => {
    setIsServiceSelectionOpen(true);
    setIsMenuOpen(false);
  };

  const handleNavClick = (href: string) => {
    if (!isHomePage && href.startsWith('#')) {
      // Navigate to home page, then scroll after navigation
      navigate('/', { state: { scrollTo: href.replace('#', '') } });
    } else {
      const targetId = href.replace('#', '');
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const handleServiceSelect = (service: any) => {
    setSelectedService(service);
    setIsServiceSelectionOpen(false);
    setIsBookingModalOpen(true);
  };

  const menuItems = [
    { label: t('nav.home', 'Home'), href: "/", isLink: true },
    { label: t('nav.services', 'Services'), href: isHomePage ? "#services" : "/services", isLink: !isHomePage },
    { label: t('nav.portfolio', 'Portfolio'), href: isHomePage ? "#portfolio" : "/portfolio", isLink: !isHomePage },
    { label: t('nav.contact', 'Contact'), href: "#contact", isLink: false },
  ];

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-border/10 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            {logoUrl ? (
              <img
                src={getOptimizedImageUrl(logoUrl, { height: 80, resize: 'contain' })}
                alt="Logo"
                className="h-10 w-auto max-w-[120px] object-contain"
              />
            ) : (
              <Camera className="h-8 w-8 text-[hsl(var(--gold-dark))]" />
            )}
            <span className="text-xl font-bold">{photographerName}</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              item.isLink ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-foreground hover:text-[hsl(var(--gold-dark))] transition-colors duration-200 font-medium"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.href)}
                  className="text-foreground hover:text-[hsl(var(--gold-dark))] transition-colors duration-200 font-medium"
                >
                  {item.label}
                </button>
              )
            ))}
          </div>

          {/* CTA & Language */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              onClick={handleBookSession}
              className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold-dark))] text-black font-semibold transition-all duration-300"
            >
              {t('hero.cta', 'Book Session')}
            </Button>
          </div>

          {/* Mobile Menu Toggle & Lang (Mobile) */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border/10 bg-white/95 backdrop-blur-md">
            <div className="py-4 space-y-4">
              {menuItems.map((item) => (
                item.isLink ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-left px-4 py-2 text-foreground hover:text-[hsl(var(--gold-dark))] hover:bg-muted/50 transition-colors duration-200 font-medium"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href)}
                    className="block w-full text-left px-4 py-2 text-foreground hover:text-[hsl(var(--gold-dark))] hover:bg-muted/50 transition-colors duration-200 font-medium"
                  >
                    {item.label}
                  </button>
                )
              ))}
              <div className="px-4 pt-2">
                <Button
                  onClick={handleBookSession}
                  className="w-full bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold-dark))] text-black font-semibold"
                >
                  {t('hero.cta', 'Book Session')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ServiceSelectionModal
        isOpen={isServiceSelectionOpen}
        onClose={() => setIsServiceSelectionOpen(false)}
        onServiceSelect={handleServiceSelect}
      />

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        service={selectedService}
      />
    </nav>
  );
}